// src/net/types.ts — CLIENT-side network contract for the M1 board API.
//
// This file owns the *wire* shapes the client expects from the backend (the Render Web Service in
// backend/, see render.yaml). It deliberately re-uses the frozen Core `BoardContent` for the happy
// path: the backend maps the LLM's is_correct->kind, enforces counts/char-limits, and sets
// source/verified, so a successful /board response IS a Core BoardContent ready to hand to the Sim.
//
// NOTHING here touches the Anthropic SDK or an API key — the key is server-side only. The client is
// a pure `fetch` consumer (no VITE_-prefixed secret; VITE_API_BASE_URL is a URL, not a secret).
import type { BoardContent } from '../core/types';

/** What the backend returns on POST /board (200). It already matches Core BoardContent. */
export type BoardResponse = BoardContent;

/** What the backend returns on GET /daily (200): today's curated/generated board + its date key. */
export interface DailyResponse {
  board: BoardContent;
  /** ISO date (UTC) the daily board is keyed to, e.g. "2026-06-21". Optional; informational. */
  date?: string;
}

/**
 * Why a fetchBoard call did not return a freshly-generated board. The client maps every failure
 * mode (network / 422 unprocessable / 429 rate-limited / 503 unavailable / timeout) onto one of
 * these so the UI can show a *specific, friendly* inline message and (where possible) still play.
 */
export type BoardErrorReason =
  | 'rate_limited' // 429 — too many novel topics too fast
  | 'unprocessable' // 422 — topic couldn't be turned into a factual, verifiable board
  | 'unavailable' // 503 / 5xx — backend or spend-ceiling/circuit-breaker down
  | 'timeout' // exceeded the ~6s budget
  | 'offline' // network error / fetch threw
  | 'generating' // 202 — verified board is generating in the background (cache-first); play a quick board now
  | 'bad_response'; // 200 but the body failed validation

/** Board-cook lifecycle signals for the loading UI: 'accepted' = the backend answered the request,
 *  'generating' = a 202 poll is in flight (board generating+verifying in the background),
 *  'verified' = a validated generated board arrived. Seed fallbacks never claim 'verified'. */
export type BoardFetchPhase = 'accepted' | 'generating' | 'verified';

/** Thrown by the net layer when a generated board is unavailable AND no seed fallback applied. */
export class BoardError extends Error {
  readonly reason: BoardErrorReason;
  /** A short, player-facing message safe to render inline (no stack/jargon). */
  readonly friendly: string;
  constructor(reason: BoardErrorReason, friendly: string) {
    super(friendly);
    this.name = 'BoardError';
    this.reason = reason;
    this.friendly = friendly;
  }
}

/** Result of fetchBoard: a board to play, plus how we got it (for UX copy/telemetry). */
export interface FetchBoardResult {
  board: BoardContent;
  /** 'generated' = live from the backend; 'seed' = offline/error fallback to a matching seed board. */
  origin: 'generated' | 'seed';
  /** When origin==='seed' because the network/gen failed, why we fell back (else undefined). */
  fallbackReason?: BoardErrorReason;
}

export interface FetchBoardOptions {
  signal?: AbortSignal;
  /** Override the ~6s default request budget (ms). */
  timeoutMs?: number;
  /** On a 202 (verified board generating in the background), poll the backend for the finished board
   *  for up to this budget (ms) before falling back. Default 12000. Set 0 to disable polling. */
  pollBudgetMs?: number;
  /** Interval between 202 polls (ms). Default 1500. */
  pollIntervalMs?: number;
  /** Called before each poll attempt (1-based) so the UI can show a "still cooking…" progress note. */
  onPoll?: (attempt: number) => void;
  /** Optional lifecycle callback so the loading overlay can advance its verify checklist on REAL
   *  phases instead of static theater. Purely additive; safe to omit. */
  onPhase?: (phase: BoardFetchPhase) => void;
}

// --- leaderboard contract (M3 retention/meta) -------------------------------
// The backend MAY expose POST /scores + GET /scores; until it does, the client falls back to a
// localStorage board (see src/net/leaderboard.ts). These shapes are the wire + UI contract.

/** Which board a score belongs to. ('weekly' = the P2.1 weekly league, resets Monday 00:00 UTC.) */
export type LeaderboardScope = 'global' | 'topic' | 'daily' | 'weekly';

/** A single leaderboard row (server- or locally-sourced). */
export interface ScoreEntry {
  name: string;
  score: number;
  /** human topic label for display (e.g. "European Capitals") */
  topicLabel?: string;
  /** normalized topic slug the score was set on */
  topicNormalized?: string;
  /** epoch ms the score was recorded (tiebreak: earlier wins) */
  at: number;
  /** true when this row is the local player's (drives highlight) — set client-side. */
  isYou?: boolean;
}

/** Payload to submit a finished run's score. */
export interface SubmitScoreInput {
  name: string;
  score: number;
  topicLabel: string;
  topicNormalized: string;
  /** true when the run was launched from the Daily button (also lands on the daily board) */
  isDaily?: boolean;
  /** the run seed, so the board can be replayed/verified later (informational) */
  seed?: number;
}

/** Query for the top-N of a scope. */
export interface TopScoresQuery {
  scope: LeaderboardScope;
  /** required when scope === 'topic' */
  topicNormalized?: string;
  limit?: number;
}

export interface LeaderboardOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

// --- board/content report contract (M4 trust backstop) ----------------------

export type ReportReason = 'wrong_answer' | 'unsafe' | 'outdated' | 'other';

export interface ReportCell {
  id?: number;
  text: string;
  displayText?: string;
  kind?: 'correct' | 'decoy' | 'wall';
}

export interface SubmitReportInput {
  topic: string;
  topicNormalized?: string;
  rule?: string;
  reason: ReportReason;
  detail?: string;
  seed?: number;
  boardIndex?: number;
  cells?: ReportCell[];
}

export interface ReportOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

// --- ghost duels (Slice B) raceable-ghost contract --------------------------
// A "ghost" is an OPT-IN recording of a finished daily run: a base64url event track + the pin fields
// (bv/seed/mutator/topic) a recipient re-checks before replaying. Uploaded at the daily score-submit
// moment; fetched by `&gr=<id>` so a friend races the actual replay. Mirrors the server ghostStore
// contract (the firewall forbids literal server-source paths in shipped maps — keep it that way).
// The wire shape carries NO id — the id is the KV key (the fetch URL / the putGhost return value).

export interface GhostRecord {
  v: 1;
  /** GHOST_BALANCE_VERSION the run was recorded under — the client version-gates PER BLOB after fetch. */
  bv: string;
  /** UTC day (YYYY-MM-DD) the run was recorded on. */
  day: string;
  /** topicNormalized the run was recorded on — must match the live board's topic to replay. */
  topic: string;
  /** the daily seed the run was recorded under. */
  seed: number;
  /** the daily mutator id (dailyMutatorId) the run was recorded under. */
  m: number;
  /** public display name (≤12 chars, server-sanitized). */
  name: string;
  /** the recorded run score (the ghost racer's target). */
  score: number;
  /** board length in ms. */
  durationMs: number;
  /** base64url-encoded GhostTrack (src/render/ghostTrack.ts). */
  track: string;
}

// --- import.meta.env typing -------------------------------------------------
// tsconfig pins `types: ["node"]`, so the project doesn't pull in vite/client's ambient
// ImportMetaEnv. Declare just the one variable the client reads. VITE_-prefixed = compiled into the
// browser bundle ON PURPOSE here: it's the public backend URL, NOT a secret.
declare global {
  interface ImportMetaEnv {
    readonly VITE_API_BASE_URL?: string;
    /** Vite-provided build flag (statically true under `vite dev`, false in a prod build → tree-shaken). */
    readonly DEV?: boolean;
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}
